import React, { useEffect, useMemo } from 'react';
import './CheatsheetModal.css';

/**
 * CheatsheetModal
 * Right-side drawer with overlay that displays the current Agent Cheatsheet.
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - agentCheatsheet: object | array (flexible schema)
 * - sectionOrder: string[] (optional, preferred ordering)
 */
export default function CheatsheetModal({ open, onClose, agentCheatsheet, sectionOrder = ['Key Tasks', 'Key Information', 'Agent Scratchpad'] }) {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const sections = useMemo(() => {
    const cs = agentCheatsheet || {};
    let _sections = [];

    if (Array.isArray(cs?.sections)) {
      _sections = cs.sections.map((s) => ({
        name: s?.name || s?.title || 'Section',
        items: Array.isArray(s?.items)
          ? s.items
          : (s?.items && typeof s.items === 'object')
            ? Object.values(s.items)
            : []
      }));
    } else if (cs && typeof cs === 'object') {
      _sections = Object.keys(cs).map((k) => {
        const val = cs[k];
        const items = Array.isArray(val)
          ? val
          : (val && typeof val === 'object')
            ? Object.values(val)
            : [];
        return { name: k, items };
      });
    }

    // Sort by preferred order, then alphabetical
    _sections.sort((a, b) => {
      const ai = sectionOrder.indexOf(a.name);
      const bi = sectionOrder.indexOf(b.name);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.name.localeCompare(b.name);
    });

    return _sections;
  }, [agentCheatsheet, sectionOrder]);

  if (!open) return null;

  return (
    <div className="cheatsheet-root" role="dialog" aria-modal="true" aria-label="Agent Cheatsheet">
      <div className="cheatsheet-overlay" onClick={onClose} />
      <aside className="cheatsheet-drawer" aria-live="polite">
        <div className="cheatsheet-header">
          <div className="cheatsheet-title">Agent Cheatsheet</div>
          <button className="cheatsheet-close" onClick={onClose} title="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.3 5.71 12 12l6.29 6.29-1.41 1.41L10.59 13.41 4.3 19.71 2.89 18.3 9.17 12 2.89 5.71 4.3 4.29l6.29 6.29 6.29-6.29z"/></svg>
          </button>
        </div>
        <div className="cheatsheet-content">
          {sections.length === 0 ? (
            <div className="cheatsheet-empty">No cheatsheet data yet. It will appear here as the agent runs.</div>
          ) : (
            <div className="cheatsheet-sections">
              {sections.map((section, idx) => (
                <div key={`${section.name}-${idx}`} className="cheatsheet-section">
                  <div className="cheatsheet-section-title">{section.name}</div>
                  {(!section.items || section.items.length === 0) ? (
                    <div className="cheatsheet-no-items">No items.</div>
                  ) : (
                    <ul className="cheatsheet-items">
                      {section.items.map((it, i) => {
                        const text = typeof it === 'string' ? it : (it?.content || it?.text || it?.value || JSON.stringify(it));
                        const pr = typeof it === 'object' && (it.priority ?? it.prio ?? it.rank);
                        const key = it?.id ?? `${section.name}-${i}`;
                        return (
                          <li key={key} className="cheatsheet-item">
                            <span className="cheatsheet-item-text">{text}</span>
                            {pr !== undefined && <span className="cheatsheet-item-priority">[P{pr}]</span>}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
